import { parseAsArrayOf, parseAsBoolean, parseAsString } from "nuqs";
import { useCallback, useMemo } from "react";

import { DEFAULT_TRAVEL_METHODS, isValidTravelMethod } from "~/modules/plannerV2/constants/travelMethods";
import { useQueryStatesWithSessionStorage } from "~/shared/hooks/useQueryStatesWithSessionStorage";

const plannerVisitFiltersParsers = {
    filterFieldworkerTransportationMethods: parseAsArrayOf(parseAsString).withDefault(DEFAULT_TRAVEL_METHODS),
    filterFieldworkerIds: parseAsArrayOf(parseAsString).withDefault([]),
    filterFieldworkerIncludeUnavailable: parseAsBoolean.withDefault(false),
};

function hasTravelMethodFilters(methods: string[]) {
    return (
        methods.length !== DEFAULT_TRAVEL_METHODS.length ||
        methods.some(method => !DEFAULT_TRAVEL_METHODS.some(m => m === method))
    );
}

export function usePlannerV2VisitFilters() {
    const [rawFilterValues, setFilterValues] = useQueryStatesWithSessionStorage(plannerVisitFiltersParsers, {
        clearOnDefault: true,
    });

    // Guard against invalid travel methods from hand-edited URLs.
    const travelMethods = useMemo(() => {
        const validMethods = rawFilterValues.filterFieldworkerTransportationMethods.filter(isValidTravelMethod);
        return validMethods.length > 0 ? validMethods : DEFAULT_TRAVEL_METHODS;
    }, [rawFilterValues.filterFieldworkerTransportationMethods]);

    const filterValues = useMemo(
        () => ({ ...rawFilterValues, filterFieldworkerTransportationMethods: travelMethods }),
        [rawFilterValues, travelMethods],
    );

    const apiParams = useMemo(() => {
        return {
            transportationMethods: travelMethods,
            includeUnavailable: filterValues.filterFieldworkerIncludeUnavailable,
            fieldworkerIds: filterValues.filterFieldworkerIds.length > 0 ? filterValues.filterFieldworkerIds : undefined,
        };
    }, [travelMethods, filterValues]);

    const toggleTravelMethod = useCallback(
        (method: string, checked: boolean) => {
            if (!isValidTravelMethod(method)) return;

            const next = checked ? [...new Set([...travelMethods, method])] : travelMethods.filter(m => m !== method);

            setFilterValues({
                filterFieldworkerTransportationMethods: next.length > 0 ? next : DEFAULT_TRAVEL_METHODS,
            });
        },
        [setFilterValues, travelMethods],
    );

    const hasAnyFilters =
        filterValues.filterFieldworkerIncludeUnavailable ||
        filterValues.filterFieldworkerIds.length > 0 ||
        hasTravelMethodFilters(filterValues.filterFieldworkerTransportationMethods);

    return {
        filterValues,
        apiParams,
        toggleTravelMethod,
        setFilterValues,
        hasAnyFilters,
    };
}
